import { uuid } from '@/utils/utils';
import {
  ok,
  fail,
  requireSelected,
  findComponentByRef,
  applyPatch,
  getBack,
} from './executorCore';

async function searchDirectives(ctx, keyword, pageNum = 1, pageSize = 20) {
  if (!ctx.request || !ctx.request.queryDirectivesList) {
    return fail('指令 API 不可用');
  }
  const res = await ctx.request.queryDirectivesList({
    instruction_name: keyword,
    page_num: pageNum,
    page_size: pageSize,
  });
  const list = (res && res.data && res.data.result) || [];
  return ok(list);
}

async function resolveInstructionsByNames(ctx, names) {
  const resolved = [];
  for (let i = 0; i < names.length; i++) {
    const r = await searchDirectives(ctx, names[i], 1, 5);
    if (!r.ok || !r.data.length) {
      return fail(`未找到指令: ${names[i]}`);
    }
    const exact = r.data.find((d) => d.instruction_name === names[i]) || r.data[0];
    resolved.push(exact);
  }
  return ok(resolved);
}

export const EXTENDED_HANDLERS = {
  async 'network.set_ip_port'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    if (!sel.comp.networkConfig) sel.comp.networkConfig = {};
    sel.comp.networkConfig.ipAdress = args.ip;
    sel.comp.networkConfig.port = args.port;
    return ok({});
  },
  async 'network.bind_directives'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    ctx.store.commit('bindDirectives', args.directives || []);
    return ok({});
  },
  async 'network.bind_directives2'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    ctx.store.commit('bindDirectives2', args.directives || []);
    return ok({});
  },
  async 'network.search_directives'(args, ctx) {
    return searchDirectives(ctx, args.keyword || '', args.pageNum, args.pageSize);
  },
  async 'network.bind_by_instruction_names'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    const r = await resolveInstructionsByNames(ctx, args.names || []);
    if (!r.ok) return r;
    ctx.store.commit('bindDirectives', r.data);
    return ok({ count: r.data.length });
  },
  async 'network.bind_grid_buttons'(args, ctx) {
    const names = args.instructionNames || [];
    const refs = args.buttonRefs || [];
    if (names.length !== refs.length) {
      return fail('按钮数量与指令数量不一致');
    }
    for (let i = 0; i < refs.length; i++) {
      const sel = requireSelected(ctx.store, refs[i], ctx);
      if (sel.error) return sel.error;
      const r = await resolveInstructionsByNames(ctx, [names[i]]);
      if (!r.ok) return r;
      ctx.store.commit('bindDirectives', r.data);
    }
    return ok({ bound: refs.length });
  },

  'event.set_mutually'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    ctx.store.commit('bindEvents', { type: 'mutuallyEvent', data: args.targetIds || [] });
    return ok({});
  },
  'event.set_jump'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    ctx.store.commit('bindEvents', { type: 'jumpEvent', data: args.pageIds || [] });
    return ok({});
  },
  'event.set_press'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    ctx.store.commit('bindEvents', { type: 'pressEvent', data: args.targetIds || [] });
    return ok({});
  },
  'event.set_press_again'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    ctx.store.commit('bindEvents', { type: 'pressAgainEvent', data: args.targetIds || [] });
    return ok({});
  },
  'event.set_click_bind'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    const target = findComponentByRef(ctx.store, args.targetRef);
    if (!target) return fail('未找到目标组件');
    if (!sel.comp.events) sel.comp.events = {};
    sel.comp.events.clickEvent = { comp: target.id, event: args.eventName };
    return ok({});
  },
  'event.clear'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    const empty = args.eventType === 'clickEvent'
      ? { comp: '', event: '' }
      : [];
    ctx.store.commit('bindEvents', { type: args.eventType, data: empty });
    return ok({});
  },
  'event.bind_mutually_group'(args, ctx) {
    const ids = (args.refs || []).map((r) => {
      const c = findComponentByRef(ctx.store, r);
      return c ? c.id : r;
    });
    ids.forEach((id) => {
      const idx = ctx.store.state.componentData.findIndex((c) => c.id === id);
      if (idx < 0) return;
      ctx.store.commit('setCurComponent', {
        component: ctx.store.state.componentData[idx],
        index: idx,
      });
      ctx.store.commit('bindEvents', { type: 'mutuallyEvent', data: ids.filter((x) => x !== id) });
    });
    return ok({ group: ids });
  },
  async 'event.video_instruction'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    const r = await resolveInstructionsByNames(ctx, args.instructionNames || []);
    if (!r.ok) return r;
    if (!sel.comp.events) sel.comp.events = { instructionDatas: [] };
    sel.comp.events.instructionDatas.push({
      id: uuid(),
      time: args.time,
      instructions: r.data,
    });
    return ok({});
  },

  'link.add'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    const target = findComponentByRef(ctx.store, args.targetRef);
    if (!target) return fail('未找到联动目标');
    if (!sel.comp.componentLinks) {
      sel.comp.componentLinks = { links: [], transitionTime: '' };
    }
    sel.comp.componentLinks.links.push({
      id: uuid(),
      comp: target.id,
      listenEvent: args.listenEvent || 'click',
      changeAttrs: (args.changeAttrs || [{ key: 'opacity', value: 1 }]).map((a) => ({
        id: uuid(),
        key: a.key,
        value: a.value,
      })),
    });
    return ok({});
  },
  'link.update'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    const link = sel.comp.componentLinks.links[args.linkIndex];
    if (!link) return fail('联动规则不存在');
    applyPatch(link, args.patch || {});
    return ok({});
  },
  'link.remove'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    sel.comp.componentLinks.links.splice(args.linkIndex, 1);
    return ok({});
  },
  'link.set_transition_time'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    sel.comp.componentLinks.transitionTime = args.ms;
    return ok({});
  },
  'link.add_change_attr'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    const link = sel.comp.componentLinks.links[args.linkIndex];
    if (!link) return fail('联动规则不存在');
    link.changeAttrs.push({ id: uuid(), key: args.key, value: args.value });
    return ok({});
  },
  'link.remove_change_attr'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    const link = sel.comp.componentLinks.links[args.linkIndex];
    if (!link) return fail('联动规则不存在');
    link.changeAttrs.splice(args.attrIndex, 1);
    return ok({});
  },
  'link.bind_on_event'(args, ctx) {
    const r = EXTENDED_HANDLERS['link.add'](args, ctx);
    if (r.ok && args.transitionTime != null) {
      EXTENDED_HANDLERS['link.set_transition_time'](
        { ref: args.ref, ms: args.transitionTime },
        ctx
      );
    }
    return r;
  },

  'plan.preview'(args, ctx) {
    if (ctx.sandbox && !ctx.sandbox.isActive()) ctx.sandbox.capture();
    return ok({ preview: true });
  },
  'plan.apply'(args, ctx) {
    if (ctx.sandbox && ctx.sandbox.isActive()) ctx.sandbox.commit();
    return ok({ applied: true });
  },
  'plan.discard'(args, ctx) {
    if (ctx.sandbox && ctx.sandbox.isActive()) ctx.sandbox.restore();
    return ok({ discarded: true });
  },
  'history.undo'(args, ctx) {
    ctx.store.commit('undo');
    return ok({});
  },
  'history.redo'(args, ctx) {
    ctx.store.commit('redo');
    return ok({});
  },
  'history.snapshot'(args, ctx) {
    ctx.store.commit('recordSnapshot');
    return ok({});
  },
  'project.set_size'(args, ctx) {
    ctx.store.commit('setProjectStyle', { width: args.width, height: args.height });
    ctx.store.commit('setCanvasStyle', { width: args.width, height: args.height });
    return ok({});
  },
  'project.save'(args, ctx) {
    if (ctx.sandbox && ctx.sandbox.isActive()) {
      return fail('请先确认或取消预览后再保存');
    }
    if (ctx.hooks.saveProject) ctx.hooks.saveProject();
    return ok({});
  },
  'project.preview'(args, ctx) {
    if (ctx.hooks.openPreview) ctx.hooks.openPreview();
    return ok({});
  },

  'attr.v_text.set'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    applyPatch(sel.comp, args.patch);
    return ok({});
  },
  'attr.picture.set'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    applyPatch(sel.comp, args.patch);
    return ok({});
  },
  'attr.line.set'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    applyPatch(sel.comp, args.patch);
    return ok({});
  },
  'attr.stream_media.set'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    applyPatch(sel.comp, args.patch);
    return ok({});
  },
  'attr.video.set'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    applyPatch(sel.comp, args.patch);
    return ok({});
  },
  'attr.music.set'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    applyPatch(sel.comp, args.patch);
    return ok({});
  },
  'attr.weather.set'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    applyPatch(sel.comp, args.patch);
    return ok({});
  },
  'attr.carousel.set'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    applyPatch(sel.comp, args.patch);
    return ok({});
  },
  'attr.date.set'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    applyPatch(sel.comp, args.patch);
    return ok({});
  },
  'attr.environment.set'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    applyPatch(sel.comp, args.patch);
    return ok({});
  },
  'attr.document.set'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    applyPatch(sel.comp, args.patch);
    return ok({});
  },
  'attr.webpage.set'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    applyPatch(sel.comp, args.patch);
    return ok({});
  },
  'attr.slider.set'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    applyPatch(sel.comp, args.patch);
    return ok({});
  },
  'attr.status_light.set'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    applyPatch(sel.comp, args.patch);
    return ok({});
  },
  'attr.status_light.monitor'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    if (!sel.comp.passiveMonitorConfig) sel.comp.passiveMonitorConfig = {};
    applyPatch(sel.comp.passiveMonitorConfig, args.patch);
    return ok({});
  },
  'attr.child_page.set'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    applyPatch(sel.comp, args.patch);
    return ok({});
  },
  'attr.visualization.set'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    applyPatch(sel.comp, args.patch);
    return ok({});
  },
  'attr.back.set'(args, ctx) {
    const back = getBack(ctx.store);
    if (!back) return fail('未找到背景');
    applyPatch(back, args.patch);
    return ok({});
  },

  'compose.group'(args, ctx) {
    ctx.store.commit('compose');
    return ok({});
  },
  'compose.ungroup'(args, ctx) {
    ctx.store.commit('decompose');
    return ok({});
  },
  'layer.bring_forward'(args, ctx) {
    if (args.ref) requireSelected(ctx.store, args.ref, ctx);
    ctx.store.commit('upComponent');
    return ok({});
  },
  'layer.send_backward'(args, ctx) {
    if (args.ref) requireSelected(ctx.store, args.ref, ctx);
    ctx.store.commit('downComponent');
    return ok({});
  },
  'layer.bring_to_front'(args, ctx) {
    if (args.ref) requireSelected(ctx.store, args.ref, ctx);
    ctx.store.commit('topComponent');
    return ok({});
  },
  'layer.send_to_back'(args, ctx) {
    if (args.ref) requireSelected(ctx.store, args.ref, ctx);
    ctx.store.commit('bottomComponent');
    return ok({});
  },
  'lock.lock'(args, ctx) {
    if (args.ref) requireSelected(ctx.store, args.ref, ctx);
    ctx.store.commit('lock');
    return ok({});
  },
  'lock.unlock'(args, ctx) {
    if (args.ref) requireSelected(ctx.store, args.ref, ctx);
    ctx.store.commit('unlock');
    return ok({});
  },
  'area.select_box'(args, ctx) {
    ctx.store.state.areaData.components = (args.refs || [])
      .map((r) => findComponentByRef(ctx.store, r))
      .filter(Boolean);
    return ok({});
  },
  'area.move_selection'(args, ctx) {
    ctx.store.commit('setAreaStyle', {
      startX: 0,
      startY: 0,
      endX: args.deltaX,
      endY: args.deltaY,
    });
    return ok({});
  },

  async 'resource.search_materials'(args, ctx) {
    if (!ctx.request || !ctx.request.queryMaterialsList) return fail('素材 API 不可用');
    const res = await ctx.request.queryMaterialsList({
      file_real_name: args.keyword || '',
      page_num: args.pageNum || 1,
      page_size: 20,
    });
    return ok((res && res.data && res.data.result) || []);
  },
  'resource.pick_for_component'(args, ctx) {
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    const field = args.field || 'backgroundImage';
    sel.comp.style[field] = args.materialId;
    return ok({});
  },
  'resource.collect_project_files'(args, ctx) {
    return ok({ files: ctx.store.state.projectFileResouces || [] });
  },
  async 'resource.upload_file'(args, ctx) {
    if (!ctx.request || !ctx.request.uploadMaterialForm) return fail('上传 API 不可用');
    const res = await ctx.request.uploadMaterialForm({
      base64: args.base64,
      fileName: args.fileName || 'ai-upload.png',
      fileType: 'picture',
    });
    return ok(res && res.data);
  },
  async 'button.render_to_image'(args, ctx) {
    if (!ctx.renderButton) return fail('按钮渲染器未就绪');
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    const images = await ctx.renderButton(sel.comp, { includePress: args.includePress !== false });
    return ok(images);
  },
  async 'button.upload_rendered'(args, ctx) {
    if (!ctx.renderButton) return fail('按钮渲染器未就绪');
    const sel = requireSelected(ctx.store, args.ref, ctx);
    if (sel.error) return sel.error;
    const images = await ctx.renderButton(sel.comp, { includePress: true });
    const uploaded = [];
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      const r = await EXTENDED_HANDLERS['resource.upload_file']({
        base64: img.base64,
        fileName: img.fileName || `${args.materialName || 'btn'}-${i}.png`,
      }, ctx);
      if (r.ok) uploaded.push(r.data);
    }
    return ok({ uploaded, images });
  },

  'validate.run'(args, ctx) {
    if (ctx.hooks.validateProject) return ctx.hooks.validateProject();
    return ok({});
  },
  'validate.locate_error'(args, ctx) {
    if (ctx.hooks.locateCanvasError) {
      ctx.hooks.locateCanvasError({
        pageId: args.pageId,
        componentId: args.componentId,
        field: args.field,
      });
    }
    return ok({});
  },
  'engine.ensure_pages_loaded'(args, ctx) {
    if (ctx.hooks.ensurePagesLoaded) ctx.hooks.ensurePagesLoaded(args.pageIds);
    return ok({});
  },
  'engine.flush_page'(args, ctx) {
    if (ctx.hooks.flushPage) ctx.hooks.flushPage();
    return ok({});
  },
  'engine.persist_page'(args, ctx) {
    if (ctx.hooks.persistPage) ctx.hooks.persistPage();
    return ok({});
  },
  'clipboard.cut'(args, ctx) {
    ctx.store.commit('cut', !!(args.refs && args.refs.length));
    return ok({});
  },
};
